export default function TryRightPanel({ summary }) {
  const {
    topicActivations = [],
    warmupResults = [],
    attempts = [],
    subjectBreakdown = {},
    avgTimeSeconds = 0,
    lastTopicComplete = null,
    currentTopicAttempts = [],
  } = summary || {};

  const isRealDiagnostic = (text) => Boolean(text) && text !== "option_label_pending";

  const lastAttempt = currentTopicAttempts[currentTopicAttempts.length - 1];
  const lastGapAttempt =
    lastAttempt && lastAttempt.result?.is_correct === false && isRealDiagnostic(lastAttempt.result?.diagnostic_text)
      ? lastAttempt
      : null;
  const subjectRows = Object.entries(subjectBreakdown);

  const topicSummary = lastTopicComplete
    ? (() => {
        const pa = lastTopicComplete.practiceAttempts || [];
        const correct = pa.filter((a) => a.result?.is_correct).length;
        const total = pa.length;
        const accuracy = total > 0 ? Math.round((correct / total) * 100) : 0;
        const gapNote = [...pa].reverse().find((a) => isRealDiagnostic(a.result?.diagnostic_text));
        return { correct, total, accuracy, gapNote };
      })()
    : null;

  return (
    <div className="rounded-2xl p-5 space-y-5" style={{ background: "#0f172a" }} data-testid="try-right-panel-dashboard">
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wider text-teal-400 mb-3" data-testid="try-right-session-activity-title">
          Session Activity
        </h4>
        {topicActivations.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="try-right-session-activity-empty">Waiting for session to begin…</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="try-right-session-activity-chips">
            {topicActivations.map((a, i) => (
              <span
                key={i}
                className="px-3 py-1.5 rounded-full border border-teal-500/40 bg-teal-500/10 text-teal-300 text-xs font-medium"
                data-testid={`try-right-activity-chip-${i}`}
              >
                {a.subject} — {a.sub_topic}
              </span>
            ))}
          </div>
        )}
      </div>

      {warmupResults.length > 0 && (
        <div className="border-t border-slate-700/60 pt-5">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-teal-400 mb-3" data-testid="try-right-concept-readiness-title">
            Concept Readiness
          </h4>
          <div className="space-y-2" data-testid="try-right-concept-readiness-rows">
            {warmupResults.map((r, i) => (
              <div key={i} className="flex items-center justify-between" data-testid={`try-right-readiness-row-${i}`}>
                <span className="text-sm text-slate-300">{r.subTopic}</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                    r.passed
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                      : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                  }`}
                  data-testid={`try-right-readiness-badge-${i}`}
                >
                  {r.passed ? "Confirmed" : "Needs Support"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {currentTopicAttempts.length > 0 && (
        <div className="border-t border-slate-700/60 pt-5">
          {topicSummary ? (
            <div data-testid="try-right-topic-summary-card">
              <h4 className="text-xs font-semibold uppercase tracking-wider text-teal-400 mb-3" data-testid="try-right-topic-summary-title">
                Topic Summary
              </h4>
              <p className="text-sm text-slate-200 font-medium mb-2" data-testid="try-right-topic-summary-subtopic">
                {lastTopicComplete.subTopic}
              </p>
              <p className="text-xs text-slate-400 mb-2" data-testid="try-right-topic-summary-accuracy">
                Practice accuracy: {topicSummary.correct}/{topicSummary.total} correct ({topicSummary.accuracy}%)
              </p>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs text-slate-400">Warmup readiness:</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${
                    lastTopicComplete.warmupPassed
                      ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                      : "bg-amber-500/15 text-amber-400 border-amber-500/30"
                  }`}
                  data-testid="try-right-topic-summary-warmup-badge"
                >
                  {lastTopicComplete.warmupPassed ? "Confirmed" : "Needs Support"}
                </span>
              </div>
              {topicSummary.gapNote && (
                <p className="text-xs text-amber-400" data-testid="try-right-topic-summary-gap-note">
                  Gap note: {topicSummary.gapNote.result.diagnostic_text}
                </p>
              )}
            </div>
          ) : (
            <>
              <h4 className="text-xs font-semibold uppercase tracking-wider text-teal-400 mb-3" data-testid="try-right-attempt-tracker-title">
                Attempt Tracker
              </h4>
              <div className="flex items-center gap-3 flex-wrap" data-testid="try-right-attempt-sequence">
                {currentTopicAttempts.map((a, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs" data-testid={`try-right-attempt-mark-${i}`}>
                    <span className="text-slate-500">Q{i + 1}</span>
                    <span className={a.result?.is_correct ? "text-emerald-400" : "text-red-400"}>
                      {a.result?.is_correct ? "✓" : "✗"}
                    </span>
                  </span>
                ))}
              </div>
              {lastGapAttempt && (
                <p className="mt-2 text-xs text-amber-400" data-testid="try-right-last-gap-signal">
                  Last gap signal: {lastGapAttempt.result.diagnostic_text}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {subjectRows.length > 0 && (
        <div className="border-t border-slate-700/60 pt-5">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-teal-400 mb-3" data-testid="try-right-subject-breakdown-title">
            Subject Breakdown
          </h4>
          <table className="w-full text-xs" data-testid="try-right-subject-breakdown-table">
            <thead>
              <tr className="text-slate-500 text-left">
                <th className="font-medium pb-1">Subject</th>
                <th className="font-medium pb-1">Correct</th>
                <th className="font-medium pb-1">Total</th>
                <th className="font-medium pb-1">Accuracy</th>
              </tr>
            </thead>
            <tbody>
              {subjectRows.map(([subject, { correct, total }], i) => (
                <tr key={subject} className="border-t border-slate-800" data-testid={`try-right-subject-row-${i}`}>
                  <td className="py-1.5 text-slate-300">{subject}</td>
                  <td className="py-1.5 text-slate-300">{correct}</td>
                  <td className="py-1.5 text-slate-300">{total}</td>
                  <td className="py-1.5 text-teal-400 font-semibold">{((correct / total) * 100).toFixed(0)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {attempts.length > 0 && avgTimeSeconds !== 0 && (
            <p className="mt-3 text-xs text-slate-500" data-testid="try-right-avg-time">
              Avg. time per question: {avgTimeSeconds}s <span className="italic">(approximate)</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
